import React, { createContext, useContext, useEffect, useState } from "react";
import ProductsService from "../api/services/products.service";
import { CurrencyTypes } from "../api/types/products.types";

interface CurrenciesContextType {
  currencies: CurrencyTypes[];
  currencyId: number | null;
  setCurrencyId: (id: number) => void;
  currency: string;
  setCurrency: (value: string) => void;
}

const CurrenciesContext = createContext<CurrenciesContextType>({
  currencies: [],
  currencyId: null,
  setCurrencyId: () => {},
  currency: "",
  setCurrency: () => {},
});

export const CurrenciesProvider = ({
  children,
}: {
  children: React.ReactNode;
}) => {
  const [currencies, setCurrencies] = useState<CurrencyTypes[]>([]);
  const [currency, setCurrencyState] = useState("");
  const [currencyId, setCurrencyIdState] = useState<number | null>(null);
  const [userSelected, setUserSelected] = useState(() => {
    return localStorage.getItem("userSelectedCurrency") === "true";
  });
  useEffect(() => {
    if (currency && currencyId !== null) {
      localStorage.setItem("currency", currency);
      localStorage.setItem("currencyId", String(currencyId));
      localStorage.setItem("userSelectedCurrency", String(userSelected));
    }
  }, [currency, currencyId, userSelected]);
  useEffect(() => {
    const getData = async () => {
      try {
        const res = await ProductsService.getCurrency();
        setCurrencies(res.data);

        if (res.data.length > 0) {
          const savedCurrency = localStorage.getItem("currency");
          const savedCurrencyId = localStorage.getItem("currencyId");

          if (savedCurrency && savedCurrencyId) {
            const savedCurrencyInApi = res.data.find(
              (curr: CurrencyTypes) =>
                curr.name === savedCurrency &&
                curr.id === Number(savedCurrencyId),
            );
            if (savedCurrencyInApi) {
              setCurrencyState(savedCurrency);
              setCurrencyIdState(Number(savedCurrencyId));
              return;
            }
          }

          if (!userSelected) {
            const lang = localStorage.getItem("i18nextLng") || "az";
            const langToCurrency: Record<string, string> = {
              az: "AZN",
              en: "USD",
              de: "EUR",
              es: "EUR",
            };
            const preferredCurrencyName =
              langToCurrency[lang] || res.data[0].name;
            const preferred = res.data.find(
              (c: CurrencyTypes) => c.name === preferredCurrencyName,
            );

            const target = preferred || res.data[0];
            setCurrencyState(target.name);
            setCurrencyIdState(target.id);
          }
        }
      } catch (error) {
        console.error("Error fetching currencies:", error);
      }
    };

    getData();
  }, [userSelected]);

  const setCurrency = (value: string) => {
    setCurrencyState(value);
    setUserSelected(true);
  };

  const setCurrencyId = (id: number) => {
    setCurrencyIdState(id);
    setUserSelected(true);
  };

  return (
    <CurrenciesContext.Provider
      value={{
        currencies,
        currency,
        setCurrency,
        currencyId,
        setCurrencyId,
      }}
    >
      {children}
    </CurrenciesContext.Provider>
  );
};

export const useCurrencies = () => useContext(CurrenciesContext);
